/* eslint-disable no-nested-ternary */
import { Box, FormControl, FormLabel, HStack, StackItem } from '@chakra-ui/react';
import { DateRangePreSelector, ExpertisePointSelect, LocalizedRangeDatepicker } from 'components';
import { FormControlFormSelect } from 'core';
import { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { ExpertisePoint, ExpertisePointGroupTypeLabel } from 'types';
import { t, Trans } from '@lingui/macro';
import { AdminReportPages } from '../admin.types';
import { AdminPageTitle } from '../components';
import {
  AdminReportExpertisePackages,
  AdminReportExtraPackages,
  AdminReportQueries,
  AdminReportSales,
} from './AdminReportsPages';
import { AdminReportCampaings } from './AdminReportsPages/AdminReportCampaings';

export const AdminReportPage = () => {
  const authUserInfo = useSelector((state: RootState) => state.auth.authUserInfo);
  const [selectedReportType, setSelectedReportType] = useState<string>(AdminReportPages.SALES);
  const [expertisePoint, setExpertisePoint] = useState<ExpertisePoint>();
  const [selectedCampaignType, setSelectedCampaignType] = useState<string>('1');
  const [selectedBrand, setSelectedBrand] = useState<number | undefined>(() => {
    const saved = localStorage.getItem('selectedExpertisePointGroupType');
    return saved ? parseInt(saved, 10) : undefined;
  });

  const ReportComponent = useMemo(() => {
    switch (selectedReportType) {
      case AdminReportPages.SALES:
        return AdminReportSales;
      case AdminReportPages.QUERIES:
        return AdminReportQueries;
      case AdminReportPages.EXTRA_PACKAGE:
        return AdminReportExtraPackages;
      case AdminReportPages.CAMPAIGN:
        return AdminReportCampaings;
      case AdminReportPages.EXPERTISE_PACKAGE:
      default:
        return AdminReportExpertisePackages;
    }
  }, [selectedReportType]);
  const [selectedDates, setSelectedDates] = useState<Date[]>([new Date(), new Date()]);

  useEffect(() => {
    const handleExpertisePointGroupTypeChange = (event: any) => {
      const { value } = event.detail;
      setSelectedBrand(value ? parseInt(value, 10) : undefined);
    };

    window.addEventListener('expertisePointGroupTypeChanged', handleExpertisePointGroupTypeChange);
    return () => window.removeEventListener('expertisePointGroupTypeChanged', handleExpertisePointGroupTypeChange);
  }, []);
  return (
    <>
      <AdminPageTitle title={t`Raporlar`} />
      <HStack align="center" gap={10}>
        <StackItem>
          <DateRangePreSelector
            onDateRangeSelected={(startDate: Date, endDate: Date) => {
              setSelectedDates([startDate, endDate]);
            }}
          />
        </StackItem>
        {(authUserInfo?.isSuperAdmin || authUserInfo?.isAdmin) && (
          <StackItem w="300px">
            <FormControlFormSelect
              label={t`Marka`}
              value={selectedBrand || ''}
              onChange={(event) => {
                const { value } = event.target;
                const numValue = value === '' ? undefined : Number(value);
                setSelectedBrand(numValue);
                setExpertisePoint(undefined);

                // Update localStorage to sync with other components
                if (value === '') {
                  localStorage.removeItem('selectedExpertisePointGroupType');
                } else {
                  localStorage.setItem('selectedExpertisePointGroupType', value);
                }

                // Dispatch custom event to notify other components
                window.dispatchEvent(
                  new CustomEvent('expertisePointGroupTypeChanged', {
                    detail: { value: value === '' ? null : value },
                  }),
                );
              }}
            >
              <option value="">
                <Trans>Hepsi</Trans>
              </option>
              {Array.from(ExpertisePointGroupTypeLabel.entries()).map(([key, value]) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
            </FormControlFormSelect>
          </StackItem>
        )}
        {(authUserInfo?.isSuperAdmin || authUserInfo?.isAdmin) && (
          <StackItem w="300px">
            <ExpertisePointSelect
              enableAll
              groupType={selectedBrand}
              onExpertisePointSelected={(selectedExpertisePoint) => {
                setExpertisePoint(selectedExpertisePoint);
              }}
            />
          </StackItem>
        )}
      </HStack>

      <HStack align="center">
        <FormControl>
          <FormLabel>
            <Trans>Tarih Aralığı</Trans>
          </FormLabel>

          <LocalizedRangeDatepicker selectedDates={selectedDates} onDateChange={setSelectedDates} />
        </FormControl>

        <FormControlFormSelect
          label="Rapor Türü"
          value={selectedReportType}
          onChange={(event) => {
            setSelectedReportType(event.target.value);
          }}
        >
          <option value={AdminReportPages.SALES}>
            <Trans>Satışlar</Trans>
          </option>
          <option value={AdminReportPages.QUERIES}>
            <Trans>Sorgulamalar</Trans>
          </option>
          <option value={AdminReportPages.EXPERTISE_PACKAGE}>
            <Trans>Ekspertiz Paketleri</Trans>
          </option>
          <option value={AdminReportPages.EXTRA_PACKAGE}>
            <Trans>Ek Hizmet Paketleri</Trans>
          </option>
          <option value={AdminReportPages.CAMPAIGN}>
            <Trans>Kampanyalar</Trans>
          </option>
        </FormControlFormSelect>

        {selectedReportType === AdminReportPages.SALES ? (
          <FormControlFormSelect
            label={t`Kampanya Türü`}
            value={selectedCampaignType}
            onChange={(event) => {
              setSelectedCampaignType(event.target.value);
            }}
          >
            <option value="1">
              <Trans>Toplam</Trans>
            </option>
            <option value="2">
              <Trans>Occo Car</Trans>
            </option>
            <option value="3">
              <Trans>İndirimli</Trans>
            </option>
          </FormControlFormSelect>
        ) : null}
      </HStack>
      <Box pl={16} pr={16} my={8}>
        <ReportComponent
          beginDate={selectedDates[0]}
          endDate={selectedDates[1]}
          expertisePoint={expertisePoint}
          externalPackageGroupSource={selectedCampaignType === '1' || selectedCampaignType === '3' ? null : 10}
          onlyDiscountedSales={selectedCampaignType === '3'}
          groupType={selectedBrand}
        />
      </Box>
    </>
  );
};
